/**
 * Pure logic for the AI document analysis feature (spec 2026-08-02).
 * Everything here is deliberately side-effect free so the pieces that decide
 * what gets snapshotted onto analysis rows are directly unit-testable.
 */

/** Mime types the ai-document-verifier accepts. */
export const ANALYSABLE_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
] as const;

/** The verifier rejects files over 10 MB. */
export const MAX_ANALYSABLE_BYTES = 10 * 1024 * 1024;

/** Below this confidence the UI flags "Low — review manually" (reference client). */
export const LOW_CONFIDENCE_THRESHOLD = 70;

/**
 * Clean a question's ai_criteria for /verify. Input is `unknown` because it
 * arrives from a jsonb column. Entries are trimmed; empties, non-strings and
 * exact duplicates are dropped. The result is what gets snapshotted onto every
 * analysis row. (Until spec 2026-08-04 this merged a global settings list
 * ahead of the question's own — the global half is gone; per-question
 * ai_enabled + ai_criteria are the only source now.)
 */
export function resolveCriteria(questionCriteria: unknown): string[] {
  return [
    ...new Set(
      Array.isArray(questionCriteria)
        ? questionCriteria
            .filter((v): v is string => typeof v === 'string')
            .map((v) => v.trim())
            .filter(Boolean)
        : [],
    ),
  ];
}

/**
 * Whether a submission_files row can be sent to /verify. A failure reason is
 * recorded on a `skipped` analysis row — never silently dropped, so "no AI
 * result" is never ambiguous.
 */
export function isAnalysable(file: {
  mime_type: string;
  size_bytes: number;
}): { ok: true } | { ok: false; reason: string } {
  if (!(ANALYSABLE_MIME_TYPES as readonly string[]).includes(file.mime_type)) {
    return { ok: false, reason: `unsupported file type: ${file.mime_type}` };
  }
  if (file.size_bytes > MAX_ANALYSABLE_BYTES) {
    return { ok: false, reason: `file is ${file.size_bytes} bytes (max 10 MB)` };
  }
  return { ok: true };
}

export type VerifierCriterionResult = {
  criterion: string;
  met: boolean;
  confidence: number | null;
  evidence: string | null;
};

/** document_analyses result columns, named to match the table. */
export type AnalysisResultColumns = {
  decision: 'approve' | 'reject';
  confidence: number | null;
  summary: string | null;
  document_type: string | null;
  extraction_note: string | null;
  criteria_results: VerifierCriterionResult[];
  model: string | null;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  pdf_extraction_tokens: number | null;
  neurons: number | null;
  estimated_usd: number | null;
};

const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * Map a parsed 2xx /verify body onto document_analyses columns. Persistence is
 * a direct copy of the contract, so this is mostly shape-tolerant plucking; the
 * one hard rule is that a body with no valid `decision` is a FAILURE (the
 * contract's own definition), carrying the body's `error` text when present.
 */
export function mapVerifierResponse(
  body: unknown,
): { ok: true; columns: AnalysisResultColumns } | { ok: false; error: string } {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) {
    return { ok: false, error: 'verifier returned a non-object body' };
  }
  const b = body as Record<string, unknown>;
  if (b.decision !== 'approve' && b.decision !== 'reject') {
    return { ok: false, error: str(b.error) ?? 'verifier response had no decision' };
  }
  const usage =
    typeof b._usage === 'object' && b._usage !== null && !Array.isArray(b._usage)
      ? (b._usage as Record<string, unknown>)
      : {};
  const criteria_results: VerifierCriterionResult[] = Array.isArray(b.criteria)
    ? b.criteria.flatMap((entry): VerifierCriterionResult[] => {
        if (typeof entry !== 'object' || entry === null) return [];
        const e = entry as Record<string, unknown>;
        if (typeof e.criterion !== 'string' || typeof e.met !== 'boolean') return [];
        return [
          {
            criterion: e.criterion,
            met: e.met,
            confidence: num(e.confidence),
            evidence: str(e.evidence),
          },
        ];
      })
    : [];
  return {
    ok: true,
    columns: {
      decision: b.decision,
      confidence: num(b.confidence),
      summary: str(b.summary),
      document_type: str(b.documentType),
      extraction_note: str(b._extractionNote),
      criteria_results,
      model: str(usage.model),
      prompt_tokens: num(usage.promptTokens),
      completion_tokens: num(usage.completionTokens),
      pdf_extraction_tokens: num(usage.pdfExtractionTokens),
      neurons: num(usage.neurons),
      estimated_usd: num(usage.estimatedUsd),
    },
  };
}
